import React, { PureComponent } from "react";
import Icon from "components/Icon";
import { Select } from "components/InputField";
import { ImageOption } from "containers/ChannelsPage/Forms/styledComponents";
import { colors } from "globalStyles";
import { isObject } from "utils/helpers";
import {
  resolveDisplayName,
  basename,
  mergeUploads,
  sortByPosition,
  withId,
} from "./helpers";

export default class ImageSelect extends PureComponent {
  // Pure data-shape helpers live in ./helpers so a non-UI consumer (e.g.
  // EditChannel's reseed logic) can depend on that util module instead of
  // this component. Kept as static re-exports here for backwards
  // compatibility with existing call sites/tests.
  static resolveDisplayName = resolveDisplayName;
  static basename = basename;
  static mergeUploads = mergeUploads;
  static sortByPosition = sortByPosition;
  static withId = withId;

  componentDidMount() {
    const { input: { value, onChange }, isMultiSelect } = this.props;
    if (isMultiSelect && Array.isArray(value)) {
      var result = [];
      sortByPosition(value).map(x => {
        const name = resolveDisplayName(x);
        if (name) result.push(withId({ file: { name }, selected: false }, x));
      })
      onChange(result);
    }
  }

  componentDidUpdate(prevProps) {
    const { input: { value, onChange }, isMultiSelect } = this.props;
    // A fresh upload replaces the working array with a FileList — array-LIKE
    // (numeric keys + length), not an Array. Merge it into the prior working
    // array, preserving already-staged filters. On any other update `value` is
    // already the normalized Array, so we leave it alone — which also prevents
    // an onChange feedback loop (the merge emits an Array, failing this guard).
    if (isMultiSelect && isObject(value) && value[0] && !Array.isArray(value)) {
      onChange(mergeUploads(prevProps.input.value, value));
    }
  }

  getValues = () => {
    const { input: { value }, isMultiSelect } = this.props;
    var result = null;
    if (isObject(value) && value[0]) {
      if (isMultiSelect) {
        // Sort by `position` here too — not just in componentDidMount. The very
        // first render derives the DraggableList display order from the raw API
        // array (which carries `position` but is unordered) BEFORE
        // componentDidMount's normalized onChange has committed. Sorting here
        // makes that first paint position-ordered as well. sortByPosition is a
        // stable no-op for the post-normalization working shape (those entries
        // have no finite `position`), so it never disturbs a user reorder.
        result = Array.isArray(value)
          ? sortByPosition(value)
              .map((x) => resolveDisplayName(x))
              .filter(Boolean)
          : [value[0].name];
      } else {
        result = value[0].name;
      }
    } else {
      result = value;
    }

    return result;
  };

  // Resolve the exact working entry behind a DraggableList removal.
  //
  // The row is identified by its INDEX, not its display name: two persisted
  // filters can resolve to the same name (the same image staged on the channel
  // twice), and a name-only lookup would then record whichever came first —
  // deleting an innocent filter and leaving the one the user clicked. The index
  // is resolved against the SAME projection getValues() renders from, so the
  // two are aligned by construction; the name is re-checked as a guard, and a
  // name match remains the fallback if that projection ever drifts.
  entryForRemoval({ value: name, index }) {
    const { input } = this.props;
    const ordered = sortByPosition(input.value).filter((x) =>
      resolveDisplayName(x),
    );
    const byIndex = Number.isInteger(index) ? ordered[index] : undefined;
    if (byIndex && resolveDisplayName(byIndex) === name) return byIndex;
    return input.value.find((x) => resolveDisplayName(x) === name);
  }

  // The single callback through which DraggableList reports BOTH what remains
  // and (on a delete) which row was removed.
  //
  // `removed` is `{ value, index }` for a delete and undefined for the
  // drag-reorder path — a reorder must never record a deletion. Deliberately
  // ONE callback rather than a sibling one: two handlers would each read a
  // possibly-stale `input.value` and clobber each other's write. Here the
  // delete intent is recorded (onto a separate form field) and the working
  // array rebuilt from a single read of the current value.
  listChanged = (listChanged, removed) => {
    const { input, recordDeletedFilterId } = this.props;
    if (!Array.isArray(input.value)) return;

    // Rebuild from a POOL that entries are consumed out of, rather than
    // re-finding each name in the original array. With duplicate display names
    // a `find` returns the same entry for every occurrence, which collapsed two
    // rows into one — and, worse, could keep the very entry just deleted while
    // dropping its twin, so the twin's id was never recorded and the filter
    // survived on the server. Consuming positionally maps N rows to N entries.
    const pool = [...input.value];

    if (removed) {
      const entry = this.entryForRemoval(removed);
      if (entry) {
        // Take the deleted entry out FIRST so it cannot be re-matched as a
        // survivor by a duplicate-named row.
        const at = pool.indexOf(entry);
        if (at !== -1) pool.splice(at, 1);
        // No id → a filter that only ever existed in this browser session (a
        // staged upload). There is nothing for the server to delete.
        if (recordDeletedFilterId && entry.id !== undefined && entry.id !== null) {
          recordDeletedFilterId(entry.id);
        }
      }
    }

    var newList = [];
    listChanged.map(value => {
      const at = pool.findIndex(x => resolveDisplayName(x) === value);
      if (at !== -1) newList.push(pool.splice(at, 1)[0]);
    })
    input.onChange(newList);
  };

  listSelectChanged = (listSelected) => {
    if (!Array.isArray(this.props.input.value)) return;
    var list = [];
    this.props.input.value.map((item) => {
      if (!item) return;
      const name = resolveDisplayName(item);
      if (!name) return;
      const selected = listSelected.includes(name);
      // This path REBUILDS every entry, so the id has to be carried across or
      // selecting a filter would erase the very thing that lets a later delete
      // name it.
      list.push(
        withId(
          {
            file: item.file ? item.file : { name },
            selected,
          },
          item,
        ),
      );
    });

    this.props.input.onChange(list);
  };

  render() {
    const { input, isMultiSelect } = this.props;
    const inputValue = this.getValues();
    
    return (
      <div>
        <Select
          {...this.props}
          value={inputValue}
          type="text"
          input={{ ...input, value: inputValue }}
          listChanged={this.listChanged}
          listSelectChanged={this.listSelectChanged}
          inputDisabled={false}
          readOnly
          optionsContainerClass="flex-display"
          leftIcon={
            <Icon
              size="16"
              name="image"
              fill={!isMultiSelect && input.value && colors.GREEN}
            />
          }
          CustomOption={({ value, onClick, selected, label, ...rest }) => (
            // `rest` carries the combobox pattern's option semantics from
            // Select (role="option", id, aria-selected, $active); the label
            // names the otherwise image-only row for screen readers.
            <ImageOption
              key={value}
              onClick={onClick}
              selected={selected}
              aria-label={label}
              {...rest}
            >
              <div style={{ backgroundImage: `url(${value})` }} />
            </ImageOption>
          )}
        />
      </div>
    );
  }
}
